import { toast } from 'sonner';

import { RobotStream, useRequestStreamChange } from 'hooks/ros/services/useRequestStreamChange';

import { CameraSource, DefaultCameraSource } from 'lib/api/manuel-control';
import { usePrimaryStreamSources } from 'lib/api/manuel-control';

import { logRed } from 'utils/debugger';

import { useSelectedCameraSource } from './setters';

const triggerCameraSourceToast = async (
  cameraSource: CameraSource,
  action: (source: CameraSource) => Promise<void>,
) => {
  toast.promise(() => action(cameraSource), {
    loading: `Changing Camera source to ${cameraSource.label}`,
    success: `Camera source changed to ${cameraSource.label}`,
    error: (e) => `Error when updating camera source:${e.message}`,
  });
};

export const useRobotControlCameraSource = (stream: RobotStream) => {
  const { cameraSources } = usePrimaryStreamSources();
  const [currentCameraSource, setRobotCameraSource] = useSelectedCameraSource(stream);
  const {
    loading: cameraSourceLoading,
    closeStream,
    changeStreamSource,
  } = useRequestStreamChange();

  const updateCameraSource = async (newSource: CameraSource) => {
    if (
      currentCameraSource.value === newSource.value &&
      newSource.value !== DefaultCameraSource.OFF
    ) {
      return;
    }
    try {
      setRobotCameraSource(newSource);

      if (newSource.value === DefaultCameraSource.OFF) {
        await closeStream(stream);
      } else {
        await changeStreamSource({
          channelName: stream,
          streamSource: newSource.value,
        });
      }
    } catch (e) {
      logRed('useRobotControlCameraSource:updateCameraSource:error', e);
      toast.error(`Failed to update camera source: ${e.message}`);
    } finally {
    }
  };

  const findCurrentSourceIndex = () => {
    const index = cameraSources.findIndex((s) => s.value === currentCameraSource.value);
    return { currentSourceIndex: index, lastItemIndex: cameraSources.length - 1 };
  };

  const selectNextCameraSource = async () => {
    const { currentSourceIndex, lastItemIndex } = findCurrentSourceIndex();
    let source;
    if (currentSourceIndex === lastItemIndex) {
      source = cameraSources[0];
    } else {
      source = cameraSources[currentSourceIndex + 1];
    }
    await triggerCameraSourceToast(source, updateCameraSource);
  };

  const selectPreviousCameraSource = async () => {
    const { currentSourceIndex, lastItemIndex } = findCurrentSourceIndex();
    let source;
    if (currentSourceIndex === 0) {
      source = cameraSources[lastItemIndex];
    } else {
      source = cameraSources[currentSourceIndex - 1];
    }
    await triggerCameraSourceToast(source, updateCameraSource);
  };

  const selectSourceByValue = async (value: string) => {
    const source = cameraSources?.find((source) => source.value === value);
    if (source) {
      await triggerCameraSourceToast(source, updateCameraSource);
    } else {
      toast.error(`This spot doesn't have ${value} camera source.`);
    }
  };

  const selectSpotCamStream = () => selectSourceByValue(DefaultCameraSource.SpotCamStream);
  const selectGripperCamStream = () => selectSourceByValue(DefaultCameraSource.GripperCam);

  return {
    cameraSourceLoading,
    currentCameraSource,
    cameraSources,
    updateCameraSource,
    selectNextCameraSource,
    selectPreviousCameraSource,
    selectSourceByValue,
    selectSpotCamStream,
    selectGripperCamStream,
  };
};
